import { type AuthenticationConfig, UnknownOrgUnitPolicy } from '@today/types'
import { Field, SelectField } from '@today/ui-components'
import { Button, HelpText, Switch } from '@today/ui-primitives'
import { useState } from 'react'
import { ProvisioningModeSelector } from '../provisioning-mode-selector/provisioning-mode-selector.view'

/** A full config replace: every field, without the audit fields (spec Contract, PUT). */
export type AuthConfigSubmit = Omit<AuthenticationConfig, 'updatedAt' | 'updatedBy'>

/** A provider that may be chosen as the default. */
export interface DefaultProviderOption {
	id: string
	name: string
}

/**
 * Props for {@link AuthConfigForm}.
 *
 * @property config - The stored config; the form is seeded from it once (key the form on `updatedAt` to re-seed).
 * @property defaultProviderOptions - The usable providers the page's hook allows as the default.
 * @property isBusy - True while a request is in flight; save is disabled.
 * @property error - A refusal to announce (lockout, invalid default, rejected role).
 * @property isReadOnly - The viewer may not change the config (no `tenant.manage_idp`): the MFA mandate switch and save are disabled.
 * @property onSave - Reports the full replacement config.
 */
export interface AuthConfigFormProps {
	config: AuthenticationConfig
	defaultProviderOptions: DefaultProviderOption[]
	isBusy?: boolean
	error?: string | null
	isReadOnly?: boolean
	onSave: (input: AuthConfigSubmit) => void
}

const NO_DEFAULT = ''

const POLICY_OPTIONS = [
	{ id: UnknownOrgUnitPolicy.NO_ORG_UNIT, text: 'Sign in without an org unit' },
	{ id: UnknownOrgUnitPolicy.DEFAULT_ORG_UNIT, text: 'Assign a default org unit' },
	{ id: UnknownOrgUnitPolicy.REJECT_LOGIN, text: 'Refuse the sign-in' },
]

const draftOf = ({ updatedAt: _updatedAt, updatedBy: _updatedBy, ...config }: AuthenticationConfig): AuthConfigSubmit =>
	config

/** The config as it is submitted: the org-unit id only travels with the policy that uses it. */
const submissionOf = (draft: AuthConfigSubmit): AuthConfigSubmit => {
	const usesOrgUnit = draft.unknownOrgUnitPolicy === UnknownOrgUnitPolicy.DEFAULT_ORG_UNIT
	return { ...draft, defaultOrgUnitId: usesOrgUnit ? draft.defaultOrgUnitId?.trim() || null : null }
}

type SectionProps = {
	draft: AuthConfigSubmit
	update: (patch: Partial<AuthConfigSubmit>) => void
	isBusy: boolean
}

/** The sign-in switches: tenant, password (with the linked-identity caveat) and IdP-asserted MFA (TO-63). */
function SignInSwitches({ draft, update, isBusy }: SectionProps) {
	return (
		<div className="flex flex-col gap-3">
			<Switch isSelected={draft.loginEnabled} isDisabled={isBusy} onChange={(loginEnabled) => update({ loginEnabled })}>
				Allow sign-in to this tenant
			</Switch>
			<div className="flex flex-col gap-1">
				<Switch
					isSelected={draft.localLoginEnabled}
					isDisabled={isBusy}
					onChange={(localLoginEnabled) => update({ localLoginEnabled })}
				>
					Allow email and password sign-in
				</Switch>
				<HelpText>
					When off, users sign in only through an identity provider. A user whose account is linked to your identity
					provider and who still has a password may keep using it: the sign-in cannot tell the two apart.
				</HelpText>
			</div>
			<div className="flex flex-col gap-1">
				<Switch
					isSelected={draft.requireIdpMfa}
					isDisabled={isBusy}
					onChange={(requireIdpMfa) => update({ requireIdpMfa })}
				>
					Require MFA asserted by the identity provider
				</Switch>
				<HelpText>
					When on, a sign-in through an identity provider is refused unless the provider reports that the user completed
					multi-factor authentication. Email and password sign-in is not affected.
				</HelpText>
			</div>
		</div>
	)
}

/**
 * The tenant MFA mandate (TO-593 REQ-008): every user must use MFA. Native users
 * are routed to authenticator enrolment; federated users must arrive with their
 * identity provider's MFA.
 */
function MfaMandateSwitch({ draft, update, isBusy, isReadOnly }: SectionProps & { isReadOnly: boolean }) {
	return (
		<div className="flex flex-col gap-1">
			<Switch
				isSelected={draft.requireMfa}
				isDisabled={isBusy || isReadOnly}
				onChange={(requireMfa) => update({ requireMfa })}
			>
				Require MFA for all users
			</Switch>
			<HelpText>
				Users without an authenticator app are asked to set one up at their next sign-in. Users who sign in through your
				identity provider must complete its multi-factor step.
			</HelpText>
		</div>
	)
}

/** What a login whose org unit cannot be resolved does. Stored now, applied once org units ship. */
function OrgUnitPolicyFields({ draft, update }: SectionProps) {
	return (
		<div className="flex flex-col gap-3">
			<SelectField
				label="When a user's org unit is unknown"
				description="Stored now; applied once org-unit mapping ships."
				options={POLICY_OPTIONS}
				value={draft.unknownOrgUnitPolicy}
				onValueChange={(policy) => update({ unknownOrgUnitPolicy: policy as UnknownOrgUnitPolicy })}
			/>
			{draft.unknownOrgUnitPolicy === UnknownOrgUnitPolicy.DEFAULT_ORG_UNIT ? (
				<Field
					label="Default org unit ID"
					value={draft.defaultOrgUnitId ?? ''}
					onChange={(event) => update({ defaultOrgUnitId: event.target.value })}
				/>
			) : null}
		</div>
	)
}

/**
 * A tenant's authentication config (TO-410 REQ-009 / REQ-010 / REQ-014 and
 * jit-provisioning REQ-003): the tenant-wide sign-in switch, password sign-in,
 * whether a federated sign-in needs IdP-asserted MFA (TO-657), the tenant MFA
 * mandate (TO-593), the default provider, user provisioning, and the
 * unknown-org-unit policy.
 * One save replaces the whole config.
 *
 * Pure: the page's hook sends the request and decides which providers are
 * usable as the default. The lockout guard and every other rule are the
 * server's; a refusal arrives here as `error`.
 */
export function AuthConfigForm({
	config,
	defaultProviderOptions,
	isBusy = false,
	error,
	isReadOnly = false,
	onSave,
}: AuthConfigFormProps) {
	const [draft, setDraft] = useState(() => draftOf(config))
	const update = (patch: Partial<AuthConfigSubmit>) => setDraft((current) => ({ ...current, ...patch }))
	const section = { draft, update, isBusy }

	return (
		<section className="flex flex-col gap-6" aria-label="Sign-in settings">
			<h3 className="text-ds-base font-semibold text-ds-text">Sign-in settings</h3>
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<SignInSwitches {...section} />
			<MfaMandateSwitch {...section} isReadOnly={isReadOnly} />
			<SelectField
				label="Default identity provider"
				description="Only enabled, verified providers can be the default."
				options={[
					{ id: NO_DEFAULT, text: 'None' },
					...defaultProviderOptions.map(({ id, name }) => ({ id, text: name })),
				]}
				value={draft.defaultProviderId ?? NO_DEFAULT}
				onValueChange={(id) => update({ defaultProviderId: id === NO_DEFAULT ? null : id })}
			/>
			<ProvisioningModeSelector value={draft} isDisabled={isBusy} onChange={(provisioning) => update(provisioning)} />
			<OrgUnitPolicyFields {...section} />
			<div>
				<Button variant="primary" isDisabled={isBusy || isReadOnly} onPress={() => onSave(submissionOf(draft))}>
					Save sign-in settings
				</Button>
			</div>
		</section>
	)
}
